import { texToKalam, useLatexWriteOn } from 'ai-tutor-player';
import katex from 'katex';
import { Sigma } from 'lucide-react';
import { useMemo, useRef } from 'react';

import type { LatexContent, ScriptElement } from '@/lib/schema.types';

import { HandwritingText } from './HandwritingText';

// HYBRID: simple formulas flatten to handwriting (matches playback); anything
// the flattener can't represent faithfully stays KaTeX. See the player's
// LatexElement for the full rationale. Keep this flag in sync with it.
const FLATTEN_FORMULAS = true;

interface Props {
  element: ScriptElement;
  /**
   * When `true`, the formula stretches to fill its parent (used by freeform
   * mode). Otherwise it sizes to its intrinsic width with `max-w-full`.
   */
  fill?: boolean;
  /** Teach playback: write the equation on glyph-by-glyph with a pencil. */
  writing?: boolean;
  /** Target write-on duration (ms) = the equation's spoken span (audio-paced). */
  writeMs?: number;
}

function asLatex(content: unknown): LatexContent {
  if (typeof content === 'string') return { source: content };
  if (content && typeof content === 'object') {
    const c = content as LatexContent & { tex?: string };
    // Tolerate malformed/legacy content (e.g. `tex` instead of `source`, or a
    // missing source) — a bad element must never crash the whole slide.
    return { ...c, source: typeof c.source === 'string' ? c.source : (typeof c.tex === 'string' ? c.tex : '') };
  }
  return { source: '' };
}

export function LatexElement({ element, fill, writing, writeMs }: Props) {
  const data = asLatex(element.content);
  const fontSize = (element.style?.fontSize as number | undefined) ?? 18;
  const color = (element.style?.color as string | undefined) ?? '#1f2937';
  const hostRef = useRef<HTMLDivElement | null>(null);

  const flat = useMemo(
    () => (FLATTEN_FORMULAS ? texToKalam(data.source) : { text: '', lossy: true }),
    [data.source],
  );
  const useFlat = FLATTEN_FORMULAS && !flat.lossy && !!flat.text;

  const html = useMemo(() => {
    if (!data.source.trim()) return null;
    try {
      return katex.renderToString(data.source, {
        displayMode: data.displayMode ?? false,
        throwOnError: false,
        output: 'html',
        strict: 'ignore',
      });
    } catch (err) {
      return `<span style="color:#dc2626">${(err as Error).message}</span>`;
    }
  }, [data.source, data.displayMode]);

  useLatexWriteOn(hostRef, { writing: !useFlat && !!writing, fontSize, html, durationMs: writeMs });

  if (useFlat) {
    return (
      <HandwritingText
        text={flat.text}
        size={fontSize}
        color={color}
        weight={(element.style?.fontWeight as number | undefined) ?? 600}
        animate={!!writing}
        targetMs={writeMs}
      />
    );
  }

  if (!html) {
    return (
      <div
        style={fill ? { width: '100%', height: '100%' } : undefined}
        className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border-subtle bg-bg-subtle/40 text-fg-subtle"
      >
        <Sigma size={20} />
        <span className="text-[11px] font-medium">Add a TeX expression</span>
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      style={{
        fontSize,
        color,
        ...(fill ? { width: '100%', height: '100%' } : null),
      }}
      className={`katex-host overflow-x-auto leading-none ${
        data.displayMode ? 'flex w-full items-center justify-center text-center' : 'inline-block max-w-full'
      } ${fill ? 'h-full' : ''}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
